import React from "react";
import Table from "@material-ui/core/Table";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import Box from "@material-ui/core/Box";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import { makeStyles } from "@material-ui/core/styles";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import IconButton from "@material-ui/core/IconButton";
import { TextField, MuiThemeProvider, Typography } from "@material-ui/core";
import InputAdornment from "@material-ui/core/InputAdornment";
import Icon from "@material-ui/core/Icon";
import SearchIcon from "@material-ui/icons/Search";
import Button from "@material-ui/core/Button";
import theme from "../shared/ManageUsersTheme";
import { useHistory } from "react-router-dom";
import Navbar from "../shared/Navbar";
import { useDispatch } from "react-redux";
import { showNavbar } from "../../store/modules/appStateSlice";
import Paper from "@material-ui/core/Paper";
import api from "../../store/api";
import TablePagination from "@material-ui/core/TablePagination";
import LinearProgress from "@material-ui/core/LinearProgress";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import Switch from "@material-ui/core/Switch";
import debounce from "lodash/debounce";

interface Iuser {
  firstName: string;
  lastName: string;
  userProfileName: string;
  userWardName: string;
  userId: string;
}

const useStyles = makeStyles((theme) => ({
  table: {
    width: "100%",
  },
  tableHead: {
    backgroundColor: "#FFF",
    borderBottom: "1px solid #979797",
    "& > *": {
      fontSize: 18,
      fontWeight: 700,
      color: "#333333",
      verticalAlign: "bottom",
      paddingBottom: 10,
      paddingLeft: 20,
    },
  },
  input: {
    height: 43,
    backgroundColor: "#ECECEC",
  },
  buttonRoot: {
    padding: 0,
  },
  blueBtn: {
    backgroundColor: "#02489D",
    color: "#FFF",
    fontSize: 15,
    fontWeight: 700,
    height: 40,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#02489DAA",
    },
  },
  tableCell: {
    height: 30,
    fontSize: 18,
    fontWeight: 400,
  },
  loading: {
    width: "100%",
    "& > * + *": {
      marginTop: theme.spacing(2, 0, 0, 50),
    },
  },
  tableRow: {
    cursor: "pointer",
  },
  headRow: {
    paddingTop: 40,
    paddingBottom: 40,
    height: 44,
  },
  searchText: {
    height: 44,
    paddingBottom: 2,
  },
}));

const ManageUsers = () => {
  const history = useHistory();
  const classes = useStyles();
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("firstName");
  const [userList, setUserList] = React.useState<Iuser[]>([]);
  const [recordCount, setRecordCount] = React.useState(0);
  const [searchText, setSearchText] = React.useState("");
  const [showAllUsers, setShowAllUsers] = React.useState(false);

  const CreateLoadingLine = (): JSX.Element => {
    return (
      <div className={classes.loading}>
        <LinearProgress color="primary" />
      </div>
    );
  };

  const createSortHandler = (column: any) => {
    let order_str = orderBy == column ? (order === "asc" ? "desc" : "asc") : "asc";
    setOrder(order_str);
    setOrderBy(column);
    setPage(0);
  };

  const getUserList = React.useMemo(
    () =>
      debounce(async (updatePageNumber: any, sortOrder: any, sortColumn: any, keyword: string, showAllUsers: boolean) => {
        setLoading(true);
        const res = await api.getMyLocationUserList(
          `pagenumber=${
            updatePageNumber + 1
          }&pagesize=10&sortcolumn=${sortColumn}&sortdirection=${sortOrder}&searchstr=${keyword}&showAll=${showAllUsers}`
        );
        if (res !== null && res.data !== null && res.data.model !== null) {
          setUserList(res.data.model);
          setRecordCount(res.data.itemsCount);
        }
        setLoading(false);
      }, 300),
    []
  );

  const handleChangePage = (event: any, newPage: any) => {
    setPage(newPage);
  };

  React.useEffect(() => {
    dispatch(showNavbar(false));
  }, []);

  React.useEffect(() => {
    getUserList(page, order, orderBy, searchText, showAllUsers);
  }, [page, order, orderBy, searchText, showAllUsers]);

  const addUserClick = () => {
    history.push({ pathname: "/manageusers/adduser" });
  };

  const inputChangedHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    setPage(0);
  };

  const editUserClick = (event: any, id: any) => {
    if (id) {
      history.push({ pathname: `/manageusers/edit/${id}` });
    }
  };

  return (
    <React.Fragment>
      <MuiThemeProvider theme={theme}>
        <Navbar />
        <Paper style={{ height: "100%", overflowY: "scroll", minWidth: 1024, paddingBottom: 50, borderRadius: 0 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" className={classes.headRow}>
            <Box component="span" style={{ marginLeft: 37 }}>
              <Typography variant="h2">Manage Users</Typography>
              <div style={{ display: "flex", alignItems: "center", marginTop: 8 }}>
                <Switch checked={!showAllUsers} onChange={() => setShowAllUsers((prev) => !prev)} color="primary" />
                <span style={{ fontSize: 20, fontWeight: 400 }}>Hide disabled users</span>
              </div>
            </Box>
            <Box component="span" display="flex" alignItems="center">
              <TextField
                variant="outlined"
                margin="normal"
                fullWidth={false}
                className={classes.searchText}
                InputProps={{
                  className: classes.input,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon color="disabled">
                        <SearchIcon />
                      </Icon>
                    </InputAdornment>
                  ),
                }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => inputChangedHandler(e)}
              />
              <Box component="span" mx={2}>
                <Button variant="contained" className={classes.blueBtn} onClick={addUserClick}>
                  Add User
                </Button>
              </Box>
            </Box>
          </Box>
          <Table className={classes.table}>
            <Box margin={0}>
              <Table size="small">
                <TableHead>
                  <TableRow className={classes.tableHead}>
                    <TableCell style={{ width: "28%", paddingLeft: 37 }}>
                      Name
                      {order == "asc" ? (
                        <IconButton classes={{ root: classes.buttonRoot }} onClick={(e) => createSortHandler(orderBy)}>
                          <KeyboardArrowDownIcon />
                        </IconButton>
                      ) : (
                        <IconButton classes={{ root: classes.buttonRoot }} onClick={(e) => createSortHandler(orderBy)}>
                          <KeyboardArrowUpIcon />
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell style={{ width: "44%" }}>Profile</TableCell>
                    <TableCell style={{ width: "28%" }}>Ward</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? null : userList.length > 0 ? (
                    userList.map((user) => (
                      <TableRow onClick={(event) => editUserClick(event, user.userId)} className={classes.tableRow} hover>
                        <TableCell component="th" scope="row" style={{ width: "28%", paddingLeft: 37 }} className={classes.tableCell}>
                          {user.firstName} {user.lastName}
                        </TableCell>
                        <TableCell component="th" scope="row" style={{ width: "44%" }} className={classes.tableCell}>
                          {user.userProfileName}
                        </TableCell>
                        <TableCell component="th" scope="row" style={{ width: "28%" }} className={classes.tableCell}>
                          {user.userWardName}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell align="center" colSpan={5}>
                        No data available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[10]}
                component="div"
                count={recordCount}
                rowsPerPage={10}
                page={page}
                onChangePage={handleChangePage}
              />
            </Box>
          </Table>
          <Box style={{ marginTop: 5, marginBottom: 20 }}>{loading ? <CreateLoadingLine /> : null}</Box>
        </Paper>
      </MuiThemeProvider>
    </React.Fragment>
  );
};

export default React.memo(ManageUsers);
